import React, { useState, useRef } from 'react';
import api from '@/services/api';

interface Props {
  websiteId: string;
  currentUrl?: string;
  path: string;       // e.g. "services", "hero"
  onUploaded: (url: string) => void;
  onRemoved: () => void;
  aspectRatio?: string; // Tailwind aspect class, e.g. "aspect-video" or "aspect-[21/9]"
  siteBaseUrl?: string; // e.g. "https://mysite.vercel.app" — resolves relative image URLs
}

/** Resolve a potentially relative image URL to an absolute one for preview */
export function resolveImageUrl(url: string | undefined, siteBaseUrl?: string): string | undefined {
  if (!url) return undefined;
  // Already absolute
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Relative path like /images/services/plumbing.jpg — prefix with site domain
  if (siteBaseUrl && url.startsWith('/')) {
    const base = siteBaseUrl.replace(/\/$/, '');
    return `${base}${url}`;
  }
  return url;
}

const ImageUpload: React.FC<Props> = ({
  websiteId,
  currentUrl,
  path,
  onUploaded,
  onRemoved,
  aspectRatio = 'aspect-video',
  siteBaseUrl,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
      setError('Only JPEG, PNG, and WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const res = await api.post(
        `/api/websites/${websiteId}/images?path=${encodeURIComponent(path)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      onUploaded(res.data.url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const resolvedUrl = resolveImageUrl(currentUrl, siteBaseUrl);

  if (currentUrl) {
    return (
      <div className="space-y-2">
        <div className={`relative ${aspectRatio} rounded-lg overflow-hidden border border-zinc-200 bg-zinc-100`}>
          <img
            src={resolvedUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          {isUploading && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
            className="text-xs text-accent hover:text-accent/80 font-medium disabled:opacity-50"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={() => { onRemoved(); setError(null); }}
            className="text-xs text-red-500 hover:text-red-700 font-medium"
          >
            Remove
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleInputChange}
          className="hidden"
        />
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        className={`${aspectRatio} rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-accent bg-accent/5'
            : 'border-zinc-300 hover:border-accent/50 hover:bg-zinc-50'
        } ${isUploading ? 'pointer-events-none opacity-60' : ''}`}
      >
        {isUploading ? (
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-accent border-t-transparent" />
        ) : (
          <>
            <svg className="w-8 h-8 text-zinc-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
            </svg>
            <p className="text-xs text-muted">Click or drag to upload</p>
            <p className="text-xs text-zinc-400 mt-0.5">JPEG, PNG, WebP (max 5MB)</p>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleInputChange}
        className="hidden"
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default ImageUpload;
